import { useState, useEffect, useCallback } from 'react';
import {
  Settings, Zap, ClipboardList, Activity, Save,
  Loader2, Plus, Trash2, Tag, Clock,
  Hash, Users, GitBranch, Code2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Separator } from '@easyflow/ui-frontend/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { apiClient, getPublicBaseUrl } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import TasksEmbedCodeDialog from '@/components/tasks/TasksEmbedCodeDialog';

const buildTasksEmbedCode = (formId: string): string => {
  const baseUrl = getPublicBaseUrl();
  return `<div id="edg-ticket-form"></div>\n<script>\n(function(){var s='${baseUrl}/api/tasks/public/form-by-id/${formId}';fetch(s).then(function(r){return r.json()}).then(function(d){if(!d.success||!d.data)return;var f=d.data,c=document.getElementById('edg-ticket-form');if(!c)return;var h='<form id="edg-tf" style="max-width:500px;font-family:sans-serif"><h3>'+f.name+'</h3>';(f.fields||[]).forEach(function(fl){h+='<div style="margin:8px 0"><label>'+fl.label+(fl.required?' *':'')+'</label><br>';if(fl.type==='textarea')h+='<textarea name="'+fl.label+'" style="width:100%;padding:8px;border:1px solid #ccc;border-radius:4px" '+(fl.required?'required':'')+' rows="4"></textarea>';else h+='<input type="'+(fl.type||'text')+'" name="'+fl.label+'" style="width:100%;padding:8px;border:1px solid #ccc;border-radius:4px" '+(fl.required?'required':'')+'/>';h+='</div>'});h+='<button type="submit" style="background:#6366f1;color:#fff;border:none;padding:10px 20px;border-radius:6px;cursor:pointer;margin-top:8px">Invia</button></form>';c.innerHTML=h;document.getElementById('edg-tf').onsubmit=function(e){e.preventDefault();var fd={};new FormData(this).forEach(function(v,k){fd[k]=v});fetch(s+'/submit',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({data:fd})}).then(function(r){return r.json()}).then(function(r){if(r.success)c.innerHTML='<p style="color:green">'+(r.message||'Inviato!')+'</p>';else c.innerHTML='<p style="color:red">Errore nell\\'invio</p>'})}})})()\n</script>`;
};

interface TaskSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TaskGlobalSettings {
  task_code_prefix?: string;
  default_visibility?: string;
  auto_personal_boards?: boolean;
  default_board_id?: string | null;
  [key: string]: unknown;
}

interface BoardOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface AutomationRuleItem {
  id: string;
  name?: string;
  is_active: boolean;
  trigger_type?: string;
  action_type?: string;
  linked_board_count?: number | string;
  [key: string]: unknown;
}

interface EmbedFormItem {
  id: string;
  name?: string;
  is_active: boolean;
  fields?: FormField[];
  linked_board_count?: number | string;
  [key: string]: unknown;
}

interface FormField {
  label: string;
  type: string;
  required: boolean;
}

interface TaskTagItem {
  id: string;
  name?: string;
  color?: string;
  [key: string]: unknown;
}

interface WorkflowDashboardStats {
  automation_rules?: { active?: number | string; total?: number | string };
  embed_forms?: { active?: number | string; total?: number | string };
  [key: string]: unknown;
}

// Task #3713 — righe dashboard dai DTO condivisi (i metodi task-settings
// dell'apiClient sono tipizzati).
import type {
  TaskSettingsWorkflowLink as DashboardWorkflowLink,
  TaskSettingsFormLink as DashboardFormLink,
  TaskSettingsAutomationLink as DashboardAutomationLink,
  TaskSettingsWorkflowRun as DashboardWorkflowRun,
} from '@easyflow/shared';

export default function TaskSettingsModal({ open, onOpenChange }: TaskSettingsModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const [settings, setSettings] = useState<TaskGlobalSettings>({
    task_code_prefix: 'TASK',
    default_visibility: 'all',
    auto_personal_boards: false,
    default_board_id: null,
  });

  const [boards, setBoards] = useState<BoardOption[]>([]);
  const [automationRules, setAutomationRules] = useState<AutomationRuleItem[]>([]);
  const [embedForms, setEmbedForms] = useState<EmbedFormItem[]>([]);
  const [dashboard, setDashboard] = useState<WorkflowDashboardStats | null>(null);
  const [workflowLinks, setWorkflowLinks] = useState<DashboardWorkflowLink[]>([]);
  const [formLinks, setFormLinks] = useState<DashboardFormLink[]>([]);
  const [automationLinks, setAutomationLinks] = useState<DashboardAutomationLink[]>([]);
  const [workflowRuns, setWorkflowRuns] = useState<DashboardWorkflowRun[]>([]);

  const [showNewAutomation, setShowNewAutomation] = useState(false);
  const [newRule, setNewRule] = useState<{ name: string; trigger_type: string; action_type: string; action_config: Record<string, unknown> }>({ name: '', trigger_type: 'task_created', action_type: 'send_notification', action_config: {} });
  const [showNewForm, setShowNewForm] = useState(false);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedDialogFormName, setEmbedDialogFormName] = useState<string>('');
  const [embedDialogCode, setEmbedDialogCode] = useState<string>('');
  const [newForm, setNewForm] = useState<{ name: string; fields: FormField[] }>({ name: '', fields: [{ label: 'Nome', type: 'text', required: true }, { label: 'Email', type: 'email', required: true }, { label: 'Messaggio', type: 'textarea', required: true }] });

  const [tags, setTags] = useState<TaskTagItem[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366f1');

  const loadSettings = useCallback(async () => {
    try {
      const [settingsRes, boardsRes] = await Promise.all([
        apiClient.get<{ task_code_prefix?: string; default_visibility?: string; auto_personal_boards?: boolean; default_board_id?: string | null; [key: string]: unknown }>('/tasks/settings'),
        apiClient.get<Array<{ id: string; name?: string; [key: string]: unknown }>>('/tasks/boards/all'),
      ]);
      if (settingsRes.success && settingsRes.data) setSettings(settingsRes.data);
      if (boardsRes.success) setBoards(boardsRes.data ?? []);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    try {
      const [rulesRes, formsRes] = await Promise.all([
        apiClient.get<Array<{ id: string; name?: string; is_active: boolean; trigger_type?: string; action_type?: string; linked_board_count?: number | string; [key: string]: unknown }>>('/tasks/automation-rules'),
        apiClient.get<Array<{ id: string; name?: string; is_active: boolean; fields?: Array<{ label: string; type: string; required: boolean }>; linked_board_count?: number | string; [key: string]: unknown }>>('/tasks/embed-forms'),
      ]);
      if (rulesRes.success) setAutomationRules(rulesRes.data ?? []);
      if (formsRes.success) setEmbedForms(formsRes.data ?? []);
    } catch {}
  }, []);

  const loadDashboard = useCallback(async () => {
    const results = await Promise.allSettled([
      apiClient.get<{ automation_rules?: { active?: number | string; total?: number | string }; embed_forms?: { active?: number | string; total?: number | string }; [key: string]: unknown }>('/tasks/settings/workflow-dashboard'),
      apiClient.getTaskWorkflowLinks(),
      apiClient.getTaskFormLinks(),
      apiClient.getTaskAutomationLinks(),
      apiClient.getTaskWorkflowRuns({ limit: 30 }),
    ]);
    const [dashRes, wlRes, flRes, alRes, wrRes] = results;
    if (dashRes.status === 'fulfilled' && dashRes.value.success && dashRes.value.data) setDashboard(dashRes.value.data);
    if (wlRes.status === 'fulfilled' && wlRes.value.success) {
      // Task #3713 — metodi tipizzati (risposta top-level { success, links|runs }): niente cast.
      setWorkflowLinks(wlRes.value.links || []);
    }
    if (flRes.status === 'fulfilled' && flRes.value.success) {
      setFormLinks(flRes.value.links || []);
    }
    if (alRes.status === 'fulfilled' && alRes.value.success) {
      setAutomationLinks(alRes.value.links || []);
    }
    if (wrRes.status === 'fulfilled' && wrRes.value.success) {
      setWorkflowRuns(wrRes.value.runs || []);
    }
  }, []);

  const loadTags = useCallback(async () => {
    try {
      const res = await apiClient.get<Array<{ id: string; name?: string; color?: string; [key: string]: unknown }>>('/tasks/tags');
      if (res.success) setTags(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (open) {
      setLoading(true);
      loadSettings();
      loadCatalog();
      loadDashboard();
      loadTags();
    }
  }, [open, loadSettings, loadCatalog, loadDashboard, loadTags]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put<{ task_code_prefix?: string; default_visibility?: string; auto_personal_boards?: boolean; default_board_id?: string | null; [key: string]: unknown }>('/tasks/settings', settings);
      if (res.success && res.data) {
        setSettings(res.data);
        toast({ title: 'Impostazioni salvate' });
      }
    } catch {
      toast({ title: 'Errore nel salvataggio', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAutomation = async () => {
    if (!newRule.name.trim()) return;
    try {
      const res = await apiClient.post('/tasks/automation-rules', newRule);
      if (res.success) {
        toast({ title: 'Automazione creata' });
        setShowNewAutomation(false);
        setNewRule({ name: '', trigger_type: 'task_created', action_type: 'send_notification', action_config: {} });
        loadCatalog();
      }
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleToggleAutomation = async (ruleId: string, isActive: boolean) => {
    try {
      await apiClient.put(`/tasks/automation-rules/${ruleId}`, { is_active: !isActive });
      loadCatalog();
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleDeleteAutomation = async (ruleId: string) => {
    if (!confirm('Eliminare questa automazione dal catalogo?')) return;
    try {
      await apiClient.delete(`/tasks/automation-rules/${ruleId}`);
      toast({ title: 'Automazione eliminata' });
      loadCatalog();
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleCreateForm = async () => {
    if (!newForm.name.trim()) return;
    try {
      const slug = newForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'form';
      const res = await apiClient.post('/tasks/embed-forms', { ...newForm, slug });
      if (res.success) {
        toast({ title: 'Form creato' });
        setShowNewForm(false);
        setNewForm({ name: '', fields: [{ label: 'Nome', type: 'text', required: true }, { label: 'Email', type: 'email', required: true }, { label: 'Messaggio', type: 'textarea', required: true }] });
        loadCatalog();
      }
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleToggleForm = async (formId: string, isActive: boolean) => {
    try {
      await apiClient.put(`/tasks/embed-forms/${formId}`, { is_active: !isActive });
      loadCatalog();
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleDeleteForm = async (formId: string) => {
    if (!confirm('Eliminare questo form dal catalogo?')) return;
    try {
      await apiClient.delete(`/tasks/embed-forms/${formId}`);
      toast({ title: 'Form eliminato' });
      loadCatalog();
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const res = await apiClient.post('/tasks/tags', { name: newTagName.trim(), color: newTagColor });
      if (res.success) {
        toast({ title: 'Tag creato' });
        setNewTagName('');
        loadTags();
      }
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleRemoveWorkflowLink = async (triggerId: string) => {
    if (!confirm('Dissociare questo workflow dalla board?')) return;
    try {
      const res = await apiClient.removeTaskWorkflowLink(triggerId);
      if (res.success) {
        toast({ title: 'Workflow dissociato' });
        loadDashboard();
      }
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleRemoveFormLink = async (linkId: string) => {
    if (!confirm('Dissociare questo form dalla board?')) return;
    try {
      const res = await apiClient.removeTaskFormLink(linkId);
      if (res.success) {
        toast({ title: 'Form dissociato' });
        loadDashboard();
      }
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleRemoveAutomationLink = async (linkId: string) => {
    if (!confirm('Dissociare questa automazione dalla board?')) return;
    try {
      const res = await apiClient.removeTaskAutomationLink(linkId);
      if (res.success) {
        toast({ title: 'Automazione dissociata' });
        loadDashboard();
      }
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleDeleteTag = async (tagId: string) => {
    if (!confirm('Eliminare questo tag?')) return;
    try {
      await apiClient.delete(`/tasks/tags/${tagId}`);
      toast({ title: 'Tag eliminato' });
      loadTags();
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-sm">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">Impostazioni Task</DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Configurazione globale del sistema task</p>
              </div>
            </div>
            <Button onClick={handleSaveSettings} disabled={saving} size="sm" className="shadow-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salva impostazioni
            </Button>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Caricamento impostazioni...</p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
            <TabsList className="w-full grid grid-cols-5 h-10 bg-muted/50 rounded-lg p-1">
              <TabsTrigger value="general" className="text-xs gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-sm"><Settings className="w-3.5 h-3.5" />Generale</TabsTrigger>
              <TabsTrigger value="automations" className="text-xs gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-sm"><Zap className="w-3.5 h-3.5" />Automazioni</TabsTrigger>
              <TabsTrigger value="forms" className="text-xs gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-sm"><ClipboardList className="w-3.5 h-3.5" />Form</TabsTrigger>
              <TabsTrigger value="tags" className="text-xs gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-sm"><Tag className="w-3.5 h-3.5" />Tag</TabsTrigger>
              <TabsTrigger value="dashboard" className="text-xs gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-sm"><Activity className="w-3.5 h-3.5" />Dashboard</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="mt-4 space-y-4">
              <Card className="border-0 shadow-sm bg-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-indigo-500" />
                    <CardTitle className="text-sm font-semibold">Codifica e Navigazione</CardTitle>
                  </div>
                  <p className="text-xs text-muted-foreground">Configura come vengono identificati e visualizzati i task</p>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-foreground">Prefisso Codice Task</Label>
                      <Input
                        value={settings.task_code_prefix || 'TASK'}
                        onChange={e => setSettings((prev) => ({ ...prev, task_code_prefix: e.target.value.toUpperCase() }))}
                        placeholder="TASK"
                        maxLength={20}
                        className="h-9"
                      />
                      <p className="text-[11px] text-muted-foreground">Ogni task avra un codice univoco come <span className="font-mono text-indigo-600 dark:text-indigo-300">{settings.task_code_prefix || 'TASK'}-001</span></p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-foreground">Board Predefinita</Label>
                      <Select
                        value={settings.default_board_id || '__none__'}
                        onValueChange={v => setSettings((prev) => ({ ...prev, default_board_id: v === '__none__' ? null : v }))}
                      >
                        <SelectTrigger className="h-9"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Nessuna (auto)</SelectItem>
                          {boards.map(b => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground">La board mostrata per default all'apertura della sezione Task</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm bg-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-500" />
                    <CardTitle className="text-sm font-semibold">Personalizzazione Utenti</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">Board Personali Automatiche</Label>
                      <p className="text-[11px] text-muted-foreground">Crea automaticamente una board personale per ogni utente del tenant</p>
                    </div>
                    <Switch
                      checked={settings.auto_personal_boards || false}
                      onCheckedChange={v => setSettings((prev) => ({ ...prev, auto_personal_boards: v }))}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="automations" className="mt-4">
              <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div>
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" />
                      Catalogo Automazioni
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Regole riutilizzabili collegabili a piu board dalle impostazioni di ciascuna</p>
                  </div>
                  <Button size="sm" variant="outline" className="shadow-sm" onClick={() => setShowNewAutomation(true)}>
                    <Plus className="w-3.5 h-3.5 mr-1" />Nuova automazione
                  </Button>
                </CardHeader>
                <CardContent className="pt-2">
                  {showNewAutomation && (
                    <div className="border rounded-lg p-4 space-y-3 bg-amber-500/10 dark:bg-amber-500/15 border-amber-500/20 mb-4">
                      <div>
                        <Label className="text-xs font-medium">Nome regola</Label>
                        <Input value={newRule.name} onChange={e => setNewRule(prev => ({ ...prev, name: e.target.value }))} placeholder="es. Notifica quando task completato" className="mt-1 h-8 text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-medium">Evento trigger</Label>
                          <Select value={newRule.trigger_type} onValueChange={v => setNewRule(prev => ({ ...prev, trigger_type: v }))}>
                            <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="task_created">Task creato</SelectItem>
                              <SelectItem value="status_changed">Stato cambiato</SelectItem>
                              <SelectItem value="task_assigned">Task assegnato</SelectItem>
                              <SelectItem value="task_completed">Task completato</SelectItem>
                              <SelectItem value="task_overdue">Task scaduto</SelectItem>
                              <SelectItem value="deadline_approaching">Scadenza vicina</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Azione da eseguire</Label>
                          <Select value={newRule.action_type} onValueChange={v => setNewRule(prev => ({ ...prev, action_type: v }))}>
                            <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="send_notification">Invia notifica</SelectItem>
                              <SelectItem value="send_whatsapp">Invia WhatsApp</SelectItem>
                              <SelectItem value="send_sms">Invia SMS</SelectItem>
                              <SelectItem value="send_email">Invia Email</SelectItem>
                              <SelectItem value="change_status">Cambia stato</SelectItem>
                              <SelectItem value="assign_user">Assegna utente</SelectItem>
                              <SelectItem value="add_tag">Aggiungi tag</SelectItem>
                              <SelectItem value="create_comment">Crea commento</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end pt-1">
                        <Button variant="outline" size="sm" onClick={() => setShowNewAutomation(false)}>Annulla</Button>
                        <Button size="sm" onClick={handleCreateAutomation} disabled={!newRule.name.trim()}>Crea automazione</Button>
                      </div>
                    </div>
                  )}

                  {automationRules.length === 0 && !showNewAutomation ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-500/10 dark:bg-amber-500/15 flex items-center justify-center">
                        <Zap className="w-6 h-6 text-amber-300" />
                      </div>
                      <p className="text-sm font-medium">Nessuna automazione configurata</p>
                      <p className="text-xs mt-1">Crea la prima automazione per attivare azioni automatiche sui task</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {automationRules.map(rule => (
                        <div key={rule.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-3 min-w-0">
                            <Switch checked={rule.is_active} onCheckedChange={() => handleToggleAutomation(rule.id, rule.is_active)} />
                            <span className={`text-sm font-medium truncate ${!rule.is_active ? 'text-muted-foreground line-through' : ''}`}>{rule.name}</span>
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              {({ task_created: 'Task creato', status_changed: 'Stato cambiato', task_assigned: 'Assegnato', task_completed: 'Completato', task_overdue: 'Scaduto', deadline_approaching: 'Scadenza vicina' } as Record<string, string>)[rule.trigger_type ?? ''] || rule.trigger_type}
                            </Badge>
                            <Badge variant="secondary" className="text-[10px] shrink-0">
                              {({ send_notification: 'Notifica', send_whatsapp: 'WhatsApp', send_sms: 'SMS', send_email: 'Email', change_status: 'Cambia stato', assign_user: 'Assegna', add_tag: 'Tag', create_comment: 'Commento' } as Record<string, string>)[rule.action_type ?? ''] || rule.action_type}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {Number(rule.linked_board_count) > 0 && (
                              <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30">
                                {rule.linked_board_count} board
                              </Badge>
                            )}
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Elimina dal catalogo" onClick={() => handleDeleteAutomation(rule.id)}>
                              <Trash2 className="w-3.5 h-3.5 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="forms" className="mt-4">
              <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div>
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 text-blue-500" />
                      Catalogo Form Embed
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Form di ticketing collegabili a piu board per raccogliere richieste esterne</p>
                  </div>
                  <Button size="sm" variant="outline" className="shadow-sm" onClick={() => setShowNewForm(true)}>
                    <Plus className="w-3.5 h-3.5 mr-1" />Nuovo Form
                  </Button>
                </CardHeader>
                <CardContent className="pt-2">
                  {showNewForm && (
                    <div className="border rounded-lg p-4 space-y-3 bg-blue-500/5 dark:bg-blue-500/10 border-blue-500/20 mb-4">
                      <div>
                        <Label className="text-xs font-medium">Nome form</Label>
                        <Input value={newForm.name} onChange={e => setNewForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Es: Richiesta Supporto" className="mt-1 h-8 text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold">Campi del form</Label>
                        <div className="space-y-1.5 mt-1.5">
                          {newForm.fields.map((field, idx) => (
                            <div key={idx} className="flex items-center gap-1.5">
                              <Input value={field.label} onChange={e => { const f = [...newForm.fields]; f[idx] = { ...(f[idx] as FormField), label: e.target.value }; setNewForm(prev => ({ ...prev, fields: f })); }} className="h-7 text-xs flex-1" placeholder="Nome campo" />
                              <select value={field.type} onChange={e => { const f = [...newForm.fields]; f[idx] = { ...(f[idx] as FormField), type: e.target.value }; setNewForm(prev => ({ ...prev, fields: f })); }} className="h-7 text-xs border rounded px-1.5 bg-card">
                                <option value="text">Testo</option>
                                <option value="email">Email</option>
                                <option value="tel">Telefono</option>
                                <option value="textarea">Testo lungo</option>
                                <option value="select">Selezione</option>
                              </select>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setNewForm(prev => ({ ...prev, fields: prev.fields.filter((_, i) => i !== idx) }))}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          ))}
                          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setNewForm(prev => ({ ...prev, fields: [...prev.fields, { label: '', type: 'text', required: false }] }))}>
                            <Plus className="w-3 h-3 mr-1" />Aggiungi campo
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end pt-1">
                        <Button variant="outline" size="sm" onClick={() => setShowNewForm(false)}>Annulla</Button>
                        <Button size="sm" onClick={handleCreateForm} disabled={!newForm.name.trim()}>Crea Form</Button>
                      </div>
                    </div>
                  )}

                  {embedForms.length === 0 && !showNewForm ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-500/10 dark:bg-blue-500/15 flex items-center justify-center">
                        <ClipboardList className="w-6 h-6 text-blue-300" />
                      </div>
                      <p className="text-sm font-medium">Nessun form configurato</p>
                      <p className="text-xs mt-1">Crea un form embed per raccogliere richieste e generare task automaticamente</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {embedForms.map(form => (
                        <div key={form.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-3 min-w-0">
                            <Switch checked={form.is_active} onCheckedChange={() => handleToggleForm(form.id, form.is_active)} />
                            <span className={`text-sm font-medium truncate ${!form.is_active ? 'text-muted-foreground line-through' : ''}`}>{form.name}</span>
                            <Badge variant="outline" className="text-[10px] shrink-0">{form.fields?.length || 0} campi</Badge>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {Number(form.linked_board_count) > 0 && (
                              <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30">
                                {form.linked_board_count} board
                              </Badge>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                                  setEmbedDialogFormName(form.name || 'Form');
                                  setEmbedDialogCode(buildTasksEmbedCode(form.id));
                                  setEmbedDialogOpen(true);
                                }}>
                                  <Code2 className="w-3.5 h-3.5 text-blue-500" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Codice Embed</TooltipContent>
                            </Tooltip>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Elimina dal catalogo" onClick={() => handleDeleteForm(form.id)}>
                              <Trash2 className="w-3.5 h-3.5 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tags" className="mt-4">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <div>
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Tag className="w-4 h-4 text-indigo-500" />
                      Gestione Tag
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Crea e gestisci i tag disponibili per categorizzare i task</p>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2 items-end">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Nome tag</Label>
                      <Input
                        placeholder="Es: Urgente, Bug, Feature..."
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        className="w-[220px] h-9"
                        onKeyDown={e => { if (e.key === 'Enter' && newTagName.trim()) handleCreateTag(); }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Colore</Label>
                      <Input
                        type="color"
                        value={newTagColor}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        className="w-12 h-9 p-1 cursor-pointer"
                      />
                    </div>
                    <Button size="sm" onClick={handleCreateTag} disabled={!newTagName.trim()} className="h-9 shadow-sm">
                      <Plus className="w-3.5 h-3.5 mr-1" />Aggiungi
                    </Button>
                  </div>
                  <Separator />
                  {tags.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-indigo-500/10 dark:bg-indigo-500/15 flex items-center justify-center">
                        <Tag className="w-6 h-6 text-indigo-300" />
                      </div>
                      <p className="text-sm font-medium">Nessun tag configurato</p>
                      <p className="text-xs mt-1">Aggiungi tag per organizzare e filtrare i task</p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <div key={tag.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-card shadow-sm hover:shadow transition-shadow" style={{ borderColor: tag.color || '#6366f1' }}>
                          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: tag.color || '#6366f1' }} />
                          <span className="text-sm font-medium">{tag.name}</span>
                          <button onClick={() => handleDeleteTag(tag.id)} className="ml-1 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors" title="Elimina tag">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="dashboard" className="mt-4">
              <div className="space-y-5">
                <div className="grid grid-cols-4 gap-3">
                  <div className="p-4 rounded-xl bg-purple-500/10 dark:bg-purple-500/15 border border-purple-500/20 text-center">
                    <GitBranch className="w-5 h-5 text-purple-500 mx-auto mb-1.5" />
                    <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{workflowLinks.length}</p>
                    <p className="text-[10px] text-purple-700/80 dark:text-purple-300/80 font-medium mt-0.5">Workflow collegati</p>
                  </div>
                  <div className="p-4 rounded-xl bg-amber-500/10 dark:bg-amber-500/15 border border-amber-500/20 text-center">
                    <Zap className="w-5 h-5 text-amber-500 mx-auto mb-1.5" />
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{dashboard?.automation_rules?.active || 0}<span className="text-sm font-normal text-amber-500">/{dashboard?.automation_rules?.total || 0}</span></p>
                    <p className="text-[10px] text-amber-700/80 dark:text-amber-300/80 font-medium mt-0.5">Automazioni</p>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-500/10 dark:bg-blue-500/15 border border-blue-500/20 text-center">
                    <ClipboardList className="w-5 h-5 text-blue-500 mx-auto mb-1.5" />
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{dashboard?.embed_forms?.active || 0}<span className="text-sm font-normal text-blue-500">/{dashboard?.embed_forms?.total || 0}</span></p>
                    <p className="text-[10px] text-blue-600 dark:text-blue-300/80 font-medium mt-0.5">Form</p>
                  </div>
                  <div className="p-4 rounded-xl bg-green-500/10 dark:bg-green-500/15 border border-green-500/20 text-center">
                    <Activity className="w-5 h-5 text-green-500 mx-auto mb-1.5" />
                    <p className="text-2xl font-bold text-green-700 dark:text-green-300">{workflowRuns.length}</p>
                    <p className="text-[10px] text-green-700/80 dark:text-green-300/80 font-medium mt-0.5">Esecuzioni recenti</p>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-base font-medium flex items-center gap-2">
                        <GitBranch className="w-4 h-4 text-purple-600 dark:text-purple-300" />
                        Workflow Collegati
                      </Label>
                      <p className="text-xs text-muted-foreground">Workflow Builder associati alle board task</p>
                    </div>
                  </div>

                  {workflowLinks.length === 0 ? (
                    <div className="py-6 text-center text-muted-foreground border border-dashed rounded-lg">
                      <GitBranch className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm font-medium">Nessun workflow collegato</p>
                      <p className="text-xs text-muted-foreground mt-1">Collega un workflow dalla pagina Board per automatizzare azioni</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {workflowLinks.map((link) => (
                        <div key={link.trigger_id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/50 hover:bg-card transition-colors">
                          <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${link.is_active && link.workflow_is_active ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-foreground">{link.workflow_name}</span>
                              {link.trigger_type && (
                                <Badge variant="outline" className="text-[10px] bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30">
                                  {link.trigger_type?.replace(/^trigger_/, '').replace(/_/g, ' ')}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-[11px] text-muted-foreground">Board: {link.board_name}</span>
                              <span className="text-[11px] text-muted-foreground">{link.total_runs || 0} esecuzioni</span>
                              {Number(link.success_count) > 0 && <span className="text-[11px] text-green-600 dark:text-green-300">{link.success_count} ok</span>}
                              {Number(link.failed_count) > 0 && <span className="text-[11px] text-red-600 dark:text-red-400">{link.failed_count} errori</span>}
                              {link.last_run_at && (
                                <span className="text-[11px] text-muted-foreground flex items-center gap-0.5">
                                  <Clock className="w-3 h-3" />{new Date(link.last_run_at).toLocaleDateString('it-IT')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 dark:text-red-400 border-red-500/30 hover:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400 h-7 text-xs px-2"
                                  onClick={() => handleRemoveWorkflowLink(link.trigger_id)}
                                >
                                  Dissocia
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Scollega questo workflow dalla board</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-base font-medium flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                        Form Collegati
                      </Label>
                      <p className="text-xs text-muted-foreground">Form embed associati alle board task</p>
                    </div>
                  </div>

                  {formLinks.length === 0 ? (
                    <div className="py-6 text-center text-muted-foreground border border-dashed rounded-lg">
                      <ClipboardList className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm font-medium">Nessun form collegato</p>
                      <p className="text-xs text-muted-foreground mt-1">Associa un form dalla pagina Board per ricevere task esterni</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {formLinks.map((link) => (
                        <div key={link.link_id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/50 hover:bg-card transition-colors">
                          <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${link.form_is_active ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-foreground">{link.form_name}</span>
                              <Badge variant={link.form_is_active ? 'default' : 'secondary'} className="text-[10px]">
                                {link.form_is_active ? 'Attivo' : 'Inattivo'}
                              </Badge>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">Board: {link.board_name} — slug: <span className="font-mono text-indigo-600 dark:text-indigo-300">{link.form_slug}</span></p>
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 dark:text-red-400 border-red-500/30 hover:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400 h-7 text-xs px-2 shrink-0"
                                onClick={() => handleRemoveFormLink(link.link_id)}
                              >
                                Dissocia
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Scollega questo form dalla board</TooltipContent>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-base font-medium flex items-center gap-2">
                        <Zap className="w-4 h-4 text-amber-500" />
                        Automazioni Collegate
                      </Label>
                      <p className="text-xs text-muted-foreground">Regole di automazione associate alle board task</p>
                    </div>
                  </div>

                  {automationLinks.length === 0 ? (
                    <div className="py-6 text-center text-muted-foreground border border-dashed rounded-lg">
                      <Zap className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm font-medium">Nessuna automazione collegata</p>
                      <p className="text-xs text-muted-foreground mt-1">Associa un'automazione dalla pagina Board</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {automationLinks.map((link) => (
                        <div key={link.link_id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/50 hover:bg-card transition-colors">
                          <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${link.rule_is_active ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-foreground">{link.rule_name}</span>
                              <Badge variant={link.rule_is_active ? 'default' : 'secondary'} className="text-[10px]">
                                {link.rule_is_active ? 'Attiva' : 'Inattiva'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-[11px] text-muted-foreground">Board: {link.board_name}</p>
                              {link.trigger_type && (
                                <Badge variant="outline" className="text-[9px] bg-amber-500/10 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30">
                                  {({ task_created: 'Creazione', status_changed: 'Cambio stato', task_assigned: 'Assegnazione', due_date_approaching: 'Scadenza' } as Record<string, string>)[link.trigger_type ?? ''] || link.trigger_type}
                                </Badge>
                              )}
                              {link.action_type && (
                                <Badge variant="outline" className="text-[9px]">
                                  → {({ send_notification: 'Notifica', send_whatsapp: 'WhatsApp', send_sms: 'SMS', send_email: 'Email', change_status: 'Cambia stato', assign_user: 'Assegna', add_tag: 'Tag', create_comment: 'Commento' } as Record<string, string>)[link.action_type ?? ''] || link.action_type}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 dark:text-red-400 border-red-500/30 hover:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400 h-7 text-xs px-2 shrink-0"
                                onClick={() => handleRemoveAutomationLink(link.link_id)}
                              >
                                Dissocia
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Scollega questa automazione dalla board</TooltipContent>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-base font-medium flex items-center gap-2">
                        <Activity className="w-4 h-4 text-green-500" />
                        Ultime Esecuzioni Workflow
                      </Label>
                      <p className="text-xs text-muted-foreground">Storico recente delle esecuzioni workflow sulle board</p>
                    </div>
                  </div>

                  {workflowRuns.length === 0 ? (
                    <div className="py-6 text-center text-muted-foreground border border-dashed rounded-lg">
                      <Activity className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm font-medium">Nessuna esecuzione recente</p>
                      <p className="text-xs text-muted-foreground mt-1">Le esecuzioni dei workflow appariranno qui</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                      {workflowRuns.map((run) => (
                        <div key={run.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-muted/30 hover:bg-card transition-colors">
                          <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${run.status === 'completed' ? 'bg-green-500' : run.status === 'failed' ? 'bg-red-500' : run.status === 'running' ? 'bg-blue-500 animate-pulse' : 'bg-muted-foreground/30'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">{run.workflow_name}</span>
                              <Badge
                                variant={run.status === 'completed' ? 'default' : run.status === 'failed' ? 'destructive' : 'secondary'}
                                className="text-[9px] shrink-0"
                              >
                                {run.status === 'completed' ? 'Completato' : run.status === 'failed' ? 'Fallito' : run.status === 'running' ? 'In corso' : run.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5">
                              <span className="text-[11px] text-muted-foreground">Board: {run.board_name}</span>
                              {run.trigger_type && (
                                <Badge variant="outline" className="text-[9px] bg-purple-500/10 dark:bg-purple-500/15 text-purple-600 dark:text-purple-300 border-purple-500/30">
                                  {run.trigger_type?.replace(/^trigger_/, '').replace(/_/g, ' ')}
                                </Badge>
                              )}
                              {run.triggered_by && <span className="text-[11px] text-muted-foreground">da: {run.triggered_by}</span>}
                            </div>
                          </div>
                          <span className="text-[11px] text-muted-foreground shrink-0 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {run.started_at ? new Date(run.started_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
    <TasksEmbedCodeDialog
      open={embedDialogOpen}
      onOpenChange={setEmbedDialogOpen}
      formName={embedDialogFormName}
      embedCode={embedDialogCode}
    />
    </>
  );
}
